import React, { useEffect, useState } from "react";
import { api } from "../../../api/apiClient";
import { useStaticData } from "../../../context/StaticDataContext";
import { TENANT_ROLE_CODES } from "../../../constants/constants";
import { notify } from "../../components-ui/toast";
import { PersonDetails, RolesLookup, ServiceAccessOption } from "../../types/types";
import PartyDetailsCard from "../manage-party/components/PartyDetailsCard";
import ContactCard, { getContactValidationErrors, getPersistedContactMethods } from "../manage-party/components/ContactCard";
import AddressCard from "../manage-party/components/AddressCard";
import PartySearch from "../../components/PartySearch";

export interface OrganisationPerson {
  partyId?: number | null; userId?: string | null; displayName: string; email: string;
  serviceIds: number[]; serviceNames: string[];
  canLogIn: boolean; permissions: string[]; person?: PersonDetails;
  invitationPending?: boolean;
}
const emptyPerson = (): PersonDetails => ({
  partyId: 0, firstName: "", lastName: "", dateOfBirth: "", gender: "", partyAddresses: [],
  contactMethods: [], contactPreferences: [], householdMemberships: [], emergencyContact: null,
});

export default function OrganisationPeopleForm({ tenantId, existing, onSaved, onCancel }: {
  tenantId: number; existing?: OrganisationPerson; onSaved: () => void; onCancel: () => void;
}) {
  const { data } = useStaticData();
  const [person, setPerson] = useState<PersonDetails>(emptyPerson);
  const [serviceIds, setServiceIds] = useState<number[]>([]);
  const [services, setServices] = useState<ServiceAccessOption[]>([]);
  const [canLogIn, setCanLogIn] = useState(false);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [roles, setRoles] = useState<RolesLookup | null>(null);
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [useExistingPerson, setUseExistingPerson] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = existing?.partyId ? `partyId=${existing.partyId}` : `userId=${encodeURIComponent(existing?.userId ?? "")}`;
    Promise.all([
      api<ServiceAccessOption[]>(`/api/platformuser/tenant-services/${tenantId}`), api<RolesLookup>("/api/StaticData/roles"),
      existing ? api<OrganisationPerson>(`/api/tenant-administration/${tenantId}/people/details?${query}`) : Promise.resolve(null),
    ]).then(([options, lookup, details]) => {
      if (cancelled) return;
      setServices(options); setRoles(lookup);
      if (details) {
        setPerson(details.person ?? emptyPerson()); setServiceIds(details.serviceIds);
        setCanLogIn(details.canLogIn); setPermissions(details.permissions); setEmail(details.email); setUserId(details.userId ?? null);
      }
    }).catch(() => { if (!cancelled) setError("Unable to load the person form. Please close it and try again."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, existing]);
  const toggle = <T,>(values: T[], value: T) => values.includes(value) ? values.filter(v => v !== value) : [...values, value];
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (useExistingPerson && !person.partyId) { notify.error("Select an existing person before saving."); return; }
    if (getContactValidationErrors(person.contactMethods, data?.contactTypes ?? []).length) {
      notify.error("Please correct invalid contact details before saving."); return;
    }
    if (canLogIn && (!email.trim() || permissions.length === 0)) { notify.error("Enter a login email and select application permissions."); return; }
    setSaving(true);
    try {
      const saved = await api<OrganisationPerson>(`/api/tenant-administration/${tenantId}/people`, { method: "POST", body: {
        person: { ...person, firstName: person.firstName.trim(), lastName: person.lastName.trim(),
          dateOfBirth: person.dateOfBirth || null, contactMethods: getPersistedContactMethods(person.contactMethods) },
        userId, email: email.trim(), serviceIds, canLogIn, permissions,
      } });
      notify.success("Organisation person saved");
      if (saved.invitationPending) notify.warning("The person was saved, but the login invitation could not be sent. Use Resend invitation to try again.");
      onSaved();
    } catch { /* API errors use the shared notification handler. */ }
    finally { setSaving(false); }
  };
  const accountAction = async (action: "resend-invite" | "password-reset") => {
    if (!userId) return;
    setSaving(true);
    try {
      await api<void>(`/api/platformuser/${userId}/${action}`, { method: "POST" });
      notify.success(action === "resend-invite" ? "Login invitation sent" : "Password reset email sent");
    } catch { /* API errors use the shared notification handler. */ }
    finally { setSaving(false); }
  };
  if (loading) return <p className="muted">Loading person details...</p>;
  if (error) return <div className="card"><p role="alert">{error}</p><button type="button" className="form-submit-button form-submit-button--secondary" onClick={onCancel}>Close</button></div>;
  return <form className="form organisation-person-form" onSubmit={save}>
    {!existing?.partyId && <div className="card">
      <label className="option-item"><input type="checkbox" checked={useExistingPerson} onChange={event => {
        setUseExistingPerson(event.target.checked); setPerson(emptyPerson()); setServiceIds([]);
        setCanLogIn(existing?.canLogIn ?? false); setPermissions(existing?.permissions ?? []);
        setUserId(existing?.userId ?? null); setEmail(existing?.email ?? "");
      }} />Use an existing person</label>
      {useExistingPerson && <PartySearch searchLabel="Find a person" onPartySelected={party => {
        if (!party || party.partyType !== "Person") { notify.error("Select a person record."); return; }
        setLoading(true);
        api<OrganisationPerson>(`/api/tenant-administration/${tenantId}/people/details?partyId=${party.partyId}`)
          .then(details => {
            setPerson(details.person ?? emptyPerson()); setServiceIds(details.serviceIds);
            setCanLogIn(existing?.canLogIn ?? details.canLogIn); setPermissions(existing?.permissions ?? details.permissions);
            setEmail(existing?.email ?? details.email); setUserId(existing?.userId ?? details.userId ?? null);
          }).catch(() => {}).finally(() => setLoading(false));
      }} />}
    </div>}
    <PartyDetailsCard details={person} onChange={setPerson} requireName />
    <ContactCard contacts={person.contactMethods} onChange={(contactMethods) => setPerson(p => ({ ...p, contactMethods }))}
      contactPreferences={person.contactPreferences} onPreferenceChange={(contactPreferences) => setPerson(p => ({ ...p, contactPreferences }))} allowTypeChange />
    <AddressCard partyId={person.partyId} addresses={person.partyAddresses} onChange={(partyAddresses) => setPerson(p => ({ ...p, partyAddresses }))} />
    <div className="card"><div className="card-title">Enrolled services</div>
      <p className="field-hint">Select the services this person belongs to. Unchecking a service ends membership and its application access; session history is retained.</p>
      {services.map(service => <label key={service.serviceId} className="option-item">
        <input type="checkbox" checked={serviceIds.includes(service.serviceId)} onChange={() => setServiceIds(toggle(serviceIds, service.serviceId))} />{service.name}
      </label>)}
      {services.length === 0 && <p className="muted">No active services are available.</p>}
    </div>
    <div className="card"><div className="card-title">Application access</div>
      <label className="option-item"><input type="checkbox" checked={canLogIn} onChange={event => {
        setCanLogIn(event.target.checked);
        if (event.target.checked) {
          if (permissions.length === 0) setPermissions([TENANT_ROLE_CODES.TENANT_ADMIN]);
          if (!email) setEmail(person.contactMethods.find(c => c.contactTypeCode === "EMAIL" || data?.contactTypes.find(t => t.contactTypeId === c.contactTypeId)?.code === "EMAIL")?.contactValue ?? "");
        }
      }} />Can log in to the application</label>
      <p className="field-hint">Leave this unchecked for members who do not use the application. Existing accounts lose access to this organisation when unchecked.</p>
      {canLogIn && <>
        <div className="form-group"><label htmlFor="organisation-person-email" className="form-label">Login email</label>
          <input id="organisation-person-email" className="form-input" type="email" value={email} disabled={Boolean(userId)} required onChange={event => setEmail(event.target.value)} /></div>
        <details className="organisation-application-permissions"><summary>Application permissions</summary>
        {roles?.tenantRoles.map(role => <label key={role.code} className="option-item">
          <input type="checkbox" checked={permissions.includes(role.code)} onChange={() => setPermissions(toggle(permissions, role.code))} />
          {role.code === TENANT_ROLE_CODES.TENANT_ADMIN ? "Manage the whole organisation" : role.name}
        </label>)}
        </details>
        {userId && <div className="form-actions">
          <button type="button" className="form-submit-button form-submit-button--secondary" disabled={saving} onClick={() => { void accountAction("resend-invite"); }}>Resend invitation</button>
          <button type="button" className="form-submit-button form-submit-button--secondary" disabled={saving} onClick={() => { void accountAction("password-reset"); }}>Reset password</button>
        </div>}
      </>}
    </div>
    <div className="form-actions">
      <button type="submit" className="form-submit-button" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
      <button type="button" className="form-submit-button form-submit-button--secondary" disabled={saving} onClick={onCancel}>Cancel</button>
    </div>
  </form>;
}
